import React from "react";

/**
 * Doze mascot: a cute sleepy crescent moon with closed happy eyes, a little
 * smile and a rosy cheek. Optional floating "z z z" and a gentle idle float.
 *
 * Used as the header logo (large, with z's) and as the per-message avatar
 * (small). The crescent is cut with an SVG mask, so each instance needs a
 * unique mask id.
 */

let __dozeIdSeq = 0;

interface DozeLogoProps {
  size?: number;
  withZ?: boolean;
  floating?: boolean;
  className?: string;
}

export function DozeLogo({
  size = 40,
  withZ = false,
  floating = true,
  className = "",
}: DozeLogoProps) {
  const [maskId] = React.useState(() => `doze-moon-${++__dozeIdSeq}`);

  return (
    <span
      className={`doze-logo ${className}`}
      style={{
        position: "relative",
        display: "inline-block",
        width: size,
        height: size,
        lineHeight: 0,
      }}
    >
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        className={floating ? "doze-float" : ""}
        aria-hidden="true"
      >
        <defs>
          <mask id={maskId}>
            <rect width="64" height="64" fill="black" />
            <circle cx="28" cy="32" r="22" fill="white" />
            <circle cx="42" cy="26" r="19" fill="black" />
          </mask>
        </defs>

        {/* crescent body */}
        <circle cx="28" cy="32" r="22" fill="#2B2A28" mask={`url(#${maskId})`} />

        {/* rosy cheek */}
        <ellipse cx="20" cy="38" rx="3.4" ry="2.3" fill="#E08A6A" opacity="0.6" />

        {/* closed happy eyes */}
        <path
          d="M16 31 Q 19.5 34.5 23 31"
          stroke="#F4F1E6"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M27 31 Q 30 33.8 33 31"
          stroke="#F4F1E6"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
        />

        {/* little smile */}
        <path
          d="M22 39 Q 26 43 30 38.5"
          stroke="#F4F1E6"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
        />
      </svg>

      {withZ && (
        <span className="doze-zzz" aria-hidden="true">
          <span className="doze-z doze-z1">z</span>
          <span className="doze-z doze-z2">z</span>
          <span className="doze-z doze-z3">z</span>
        </span>
      )}
    </span>
  );
}
